import { lodestarColors } from "@smartsheet/design-tokens";
import { color } from "@smartsheet/design-tokens-react";
import { RequiredThemeKeys, themed } from "../../util/theme";

/**
 * These colors names look a LOT like our color tokens. But they're not
 * necessarily those colors and aren't required to be.
 *
 * This uses the same color naming scheme as the colors but that's just cause
 * it's a nice naming scheme.
 *
 * For example, blueDark10 and blueDark20 are actually mapped to visBlueDark20
 * and visBlueDark30, respectively, to ensure proper 4.5:1 contrast.
 */
export const colorTheme = themed({
    // These variants are only used in Ursa.
    aqua: {
        textColor: color(`aqua--text`),
        backgroundColor: color(`aqua--background`),
        backgroundHoverColor: color(`aqua--background`),
        borderColor: color(`aqua--text-alt`),
    },
    azure: {
        textColor: color(`azure--text`),
        backgroundColor: color(`azure--background`),
        backgroundHoverColor: color(`azure--background`),
        borderColor: color(`azure--text-alt`),
    },
    gold: {
        textColor: color(`gold--text`),
        backgroundColor: color(`gold--background`),
        backgroundHoverColor: color(`gold--background`),
        borderColor: color(`gold--text-alt`),
    },
    indigo: {
        textColor: color(`indigo--text`),
        backgroundColor: color(`indigo--background`),
        backgroundHoverColor: color(`indigo--background`),
        borderColor: color(`indigo--text-alt`),
    },
    mint: {
        textColor: color(`mint--text`),
        backgroundColor: color(`mint--background`),
        backgroundHoverColor: color(`mint--background`),
        borderColor: color(`mint--text-alt`),
    },
    orchid: {
        textColor: color(`orchid--text`),
        backgroundColor: color(`orchid--background`),
        backgroundHoverColor: color(`orchid--background`),
        borderColor: color(`orchid--text-alt`),
    },
    pink: {
        textColor: color(`pink--text`),
        backgroundColor: color(`pink--background`),
        backgroundHoverColor: color(`pink--background`),
        borderColor: color(`pink--text-alt`),
    },
    rose: {
        textColor: color(`rose--text`),
        backgroundColor: color(`rose--background`),
        backgroundHoverColor: color(`rose--background`),
        borderColor: color("rose--text-alt"),
    },
    violet: {
        textColor: color(`violet--text`),
        backgroundColor: color(`violet--background`),
        backgroundHoverColor: color(`violet--background`),
        borderColor: color(`violet--text-alt`),
    },

    // These color variants are used in Lodestar AND Ursa and need to swap values between.
    yellow: {
        textColor: {
            "lodestar-light": lodestarColors.neutralDark40,
            "ursa-light": color(`yellow--text`),
        },
        backgroundColor: {
            "lodestar-light": lodestarColors.visYellow,
            "ursa-light": color(`yellow--background`),
        },
        backgroundHoverColor: {
            "lodestar-light": lodestarColors.visYellow,
            "ursa-light": color(`yellow--background`),
        },
        borderColor: {
            "lodestar-light": lodestarColors.visYellow,
            "ursa-light": color(`yellow--text-alt`),
        },
    },
    blue: {
        textColor: {
            "lodestar-light": lodestarColors.neutralDark40,
            "ursa-light": color(`blue--text`),
        },
        backgroundColor: {
            "lodestar-light": lodestarColors.visBlue,
            "ursa-light": color(`blue--background`),
        },
        backgroundHoverColor: {
            "lodestar-light": lodestarColors.visBlue,
            "ursa-light": color(`blue--background`),
        },
        borderColor: {
            "lodestar-light": lodestarColors.visBlue,
            "ursa-light": color(`blue--text-alt`),
        },
    },
    green: {
        textColor: {
            "lodestar-light": lodestarColors.neutralDark40,
            "ursa-light": color(`green--text`),
        },
        backgroundColor: {
            "lodestar-light": lodestarColors.visGreen,
            "ursa-light": color(`green--background`),
        },
        backgroundHoverColor: {
            "lodestar-light": lodestarColors.visGreen,
            "ursa-light": color(`green--background`),
        },
        borderColor: {
            "lodestar-light": lodestarColors.visGreen,
            "ursa-light": color(`green--text-alt`),
        },
    },
    neutral: {
        textColor: {
            "lodestar-light": lodestarColors.neutralLight40,
            "ursa-light": color(`neutral--text`),
        },
        backgroundColor: {
            "lodestar-light": lodestarColors.neutralDark20,
            "ursa-light": color(`neutral--background`),
        },
        backgroundHoverColor: {
            "lodestar-light": lodestarColors.neutralDark20,
            "ursa-light": color(`neutral--background`),
        },
        borderColor: {
            "lodestar-light": lodestarColors.neutralDark20,
            "ursa-light": color(`neutral--text-alt`),
        },
    },
    orange: {
        textColor: {
            "lodestar-light": lodestarColors.neutralDark40,
            "ursa-light": color(`orange--text`),
        },
        backgroundColor: {
            "lodestar-light": lodestarColors.visOrange,
            "ursa-light": color(`orange--background`),
        },
        backgroundHoverColor: {
            "lodestar-light": lodestarColors.visOrange,
            "ursa-light": color(`orange--background`),
        },
        borderColor: {
            "lodestar-light": lodestarColors.visOrange,
            "ursa-light": color(`orange--text-alt`),
        },
    },
    red: {
        textColor: {
            "lodestar-light": lodestarColors.neutralDark40,
            "ursa-light": color(`red--text`),
        },
        backgroundColor: {
            "lodestar-light": lodestarColors.visRed,
            "ursa-light": color(`red--background`),
        },
        backgroundHoverColor: {
            "lodestar-light": lodestarColors.visRed,
            "ursa-light": color(`red--background`),
        },
        borderColor: {
            "lodestar-light": lodestarColors.visRed,
            "ursa-light": color(`red--text-alt`),
        },
    },
    teal: {
        textColor: {
            "lodestar-light": lodestarColors.neutralDark40,
            "ursa-light": color(`teal--text`),
        },
        backgroundColor: {
            "lodestar-light": lodestarColors.visTeal,
            "ursa-light": color(`teal--background`),
        },
        backgroundHoverColor: {
            "lodestar-light": lodestarColors.visTeal,
            "ursa-light": color(`teal--background`),
        },
        borderColor: {
            "lodestar-light": lodestarColors.visTeal,
            "ursa-light": color(`teal--text-alt`),
        },
    },

    // These color variants are only used in Lodestar and will not have tokens associated with them.
    berryLight10: {
        textColor: lodestarColors.neutralDark40,
        backgroundColor: lodestarColors.visBerryLight10,
        backgroundHoverColor: lodestarColors.visBerryLight10,
        borderColor: lodestarColors.visBerryLight10,
    },
    berry: {
        textColor: lodestarColors.neutralDark40,
        backgroundColor: lodestarColors.visBerry,
        backgroundHoverColor: lodestarColors.visBerry,
        borderColor: lodestarColors.visBerry,
    },
    berryDark10: {
        textColor: lodestarColors.neutralLight40,
        backgroundColor: lodestarColors.visBerryDark10,
        backgroundHoverColor: lodestarColors.visBerryDark10,
        borderColor: lodestarColors.visBerryDark10,
    },
    berryDark20: {
        textColor: lodestarColors.neutralLight40,
        backgroundColor: lodestarColors.visBerryDark20,
        backgroundHoverColor: lodestarColors.visBerryDark20,
        borderColor: lodestarColors.visBerryDark20,
    },
    blueLight10: {
        textColor: lodestarColors.neutralDark40,
        backgroundColor: lodestarColors.visBlueLight10,
        backgroundHoverColor: lodestarColors.visBlueLight10,
        borderColor: lodestarColors.visBlueLight10,
    },
    blueDark10: {
        textColor: lodestarColors.neutralLight40,
        // minor deviations here between naming schema and selected color
        // to ensure an accessible contrast ratio.
        backgroundColor: lodestarColors.visBlueDark20,
        backgroundHoverColor: lodestarColors.visBlueDark20,
        borderColor: lodestarColors.visBlueDark20,
    },
    blueDark20: {
        textColor: lodestarColors.neutralLight40,
        // minor deviations here between naming schema and selected color
        // to ensure an accessible contrast ratio.
        backgroundColor: lodestarColors.visBlueDark30,
        backgroundHoverColor: lodestarColors.visBlueDark30,
        borderColor: lodestarColors.visBlueDark30,
    },
    greenLight10: {
        textColor: lodestarColors.neutralDark40,
        backgroundColor: lodestarColors.visGreenLight10,
        backgroundHoverColor: lodestarColors.visGreenLight10,
        borderColor: lodestarColors.visGreenLight10,
    },
    greenDark10: {
        textColor: lodestarColors.neutralLight40,
        backgroundColor: lodestarColors.visGreenDark10,
        backgroundHoverColor: lodestarColors.visGreenDark10,
        borderColor: lodestarColors.visGreenDark10,
    },
    greenDark20: {
        textColor: lodestarColors.neutralLight40,
        backgroundColor: lodestarColors.visGreenDark20,
        backgroundHoverColor: lodestarColors.visGreenDark20,
        borderColor: lodestarColors.visGreenDark20,
    },
    orangeLight10: {
        textColor: lodestarColors.neutralDark40,
        backgroundColor: lodestarColors.visOrangeLight10,
        backgroundHoverColor: lodestarColors.visOrangeLight10,
        borderColor: lodestarColors.visOrangeLight10,
    },
    orangeDark10: {
        textColor: lodestarColors.neutralLight40,
        backgroundColor: lodestarColors.visOrangeDark10,
        backgroundHoverColor: lodestarColors.visOrangeDark10,
        borderColor: lodestarColors.visOrangeDark10,
    },
    orangeDark20: {
        textColor: lodestarColors.neutralLight40,
        backgroundColor: lodestarColors.visOrangeDark20,
        backgroundHoverColor: lodestarColors.visOrangeDark20,
        borderColor: lodestarColors.visOrangeDark20,
    },
    purpleLight10: {
        textColor: lodestarColors.neutralDark40,
        backgroundColor: lodestarColors.visPurpleLight10,
        backgroundHoverColor: lodestarColors.visPurpleLight10,
        borderColor: lodestarColors.visPurpleLight10,
    },
    purple: {
        textColor: lodestarColors.neutralDark40,
        backgroundColor: lodestarColors.visPurple,
        backgroundHoverColor: lodestarColors.visPurple,
        borderColor: lodestarColors.visPurple,
    },
    purpleDark10: {
        textColor: lodestarColors.neutralLight40,
        backgroundColor: lodestarColors.visPurpleDark10,
        backgroundHoverColor: lodestarColors.visPurpleDark10,
        borderColor: lodestarColors.visPurpleDark10,
    },
    purpleDark20: {
        textColor: lodestarColors.neutralLight40,
        backgroundColor: lodestarColors.visPurpleDark20,
        backgroundHoverColor: lodestarColors.visPurpleDark20,
        borderColor: lodestarColors.visPurpleDark20,
    },
    redLight10: {
        textColor: lodestarColors.neutralDark40,
        backgroundColor: lodestarColors.visRedLight10,
        backgroundHoverColor: lodestarColors.visRedLight10,
        borderColor: lodestarColors.visRedLight10,
    },
    redDark10: {
        textColor: lodestarColors.neutralLight40,
        backgroundColor: lodestarColors.visRedDark10,
        backgroundHoverColor: lodestarColors.visRedDark10,
        borderColor: lodestarColors.visRedDark10,
    },
    redDark20: {
        textColor: lodestarColors.neutralLight40,
        backgroundColor: lodestarColors.visRedDark20,
        backgroundHoverColor: lodestarColors.visRedDark20,
        borderColor: lodestarColors.visRedDark20,
    },
    tealLight10: {
        textColor: lodestarColors.neutralDark40,
        backgroundColor: lodestarColors.visTealLight10,
        backgroundHoverColor: lodestarColors.visTealLight10,
        borderColor: lodestarColors.visTealLight10,
    },
    tealDark10: {
        textColor: lodestarColors.neutralLight40,
        backgroundColor: lodestarColors.visTealDark10,
        backgroundHoverColor: lodestarColors.visTealDark10,
        borderColor: lodestarColors.visTealDark10,
    },
    tealDark20: {
        textColor: lodestarColors.neutralLight40,
        backgroundColor: lodestarColors.visTealDark20,
        backgroundHoverColor: lodestarColors.visTealDark20,
        borderColor: lodestarColors.visTealDark20,
    },
    yellowLight10: {
        textColor: lodestarColors.neutralDark40,
        backgroundColor: lodestarColors.visYellowLight10,
        backgroundHoverColor: lodestarColors.visYellowLight10,
        borderColor: lodestarColors.visYellowLight10,
    },
    yellowDark10: {
        textColor: lodestarColors.neutralLight40,
        backgroundColor: lodestarColors.visYellowDark10,
        backgroundHoverColor: lodestarColors.visYellowDark10,
        borderColor: lodestarColors.visYellowDark10,
    },
    yellowDark20: {
        textColor: lodestarColors.neutralLight40,
        backgroundColor: lodestarColors.visYellowDark20,
        backgroundHoverColor: lodestarColors.visYellowDark20,
        borderColor: lodestarColors.visYellowDark20,
    },
});

export type ThemeColorNames = keyof typeof colorTheme;

export const getColors = (color: keyof typeof colorTheme, themeMode: RequiredThemeKeys) => {
    return {
        textColor: colorTheme[color].textColor(themeMode),
        backgroundColor: colorTheme[color].backgroundColor(themeMode),
        backgroundHoverColor: colorTheme[color].backgroundHoverColor(themeMode),
        borderColor: colorTheme[color].borderColor(themeMode),
    };
};

export const avatarTheme = themed({
    focusRingGap: {
        "lodestar-light": 2,
        "ursa-light": 1,
    },
    weight: {
        "lodestar-light": 700,
        "ursa-light": 600,
    },
});

interface AvatarBackgroundProps {
    backgroundColor: string;
    backgroundImage: string | undefined;
    hasError: boolean;
}

export const getBackground = (props: AvatarBackgroundProps) => {
    if (props.backgroundImage && !props.hasError) {
        return `url(${props.backgroundImage}) center center no-repeat`;
    }
    return props.backgroundColor;
};

interface AvatarBackgroundHoverProps {
    backgroundColor: string;
    hasBackgroundImage: boolean;
    hasError: boolean;
}
export const getBackgroundHoverColor = (props: AvatarBackgroundHoverProps) => {
    if (props.hasBackgroundImage && !props.hasError) {
        // Returning undefined means the CSS custom property won't be set.
        return undefined;
    }
    return props.backgroundColor;
};
